// src/pages/AdminPanel/AnalyticsDashboard.jsx
import React, { useEffect, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../services/firebase"; // Ensure correct path to firebase db
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
} from "recharts";
import { Package, Users, Ticket, Settings, PlusCircle, CreditCard, Gift, PackageCheck, Wallet2, DollarSign, ShoppingBag, TrendingUp } from 'lucide-react'; // Icons
import { Link } from 'react-router-dom'; // For navigation
import './AnalyticsDashboard.css'; // Custom CSS for fine-tuning the premium look

const AnalyticsDashboard = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalOrders: 0,
    avgOrder: 0,
  });
  const [ordersOverTime, setOrdersOverTime] = useState([]);
  const [revenueOverTime, setRevenueOverTime] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStats = async () => {
      setLoading(true);
      setError(null);
      try {
        const snapshot = await getDocs(collection(db, "orders"));
        const orders = snapshot.docs
          .map((doc) => ({ id: doc.id, ...doc.data() }))
          .filter((o) => o.createdAt && o.createdAt.seconds); // Ensure valid timestamp

        let totalRevenue = 0;
        orders.forEach(o => {
          // Assuming 'totalAmount' is stored in cents in Firestore
          totalRevenue += o.totalAmount || 0; 
        });

        const totalOrders = orders.length;
        const avgOrder = totalOrders ? totalRevenue / totalOrders : 0;

        setStats({ 
          totalRevenue: totalRevenue / 100, // Convert to dollars for display
          totalOrders, 
          avgOrder: avgOrder / 100 // Convert to dollars for display
        });

        // Group orders by date (YYYY-MM-DD)
        const grouped = orders.reduce((acc, order) => {
          const date = new Date(order.createdAt.seconds * 1000)
            .toISOString()
            .slice(0, 10); // Format as YYYY-MM-DD
          
          if (!acc[date]) acc[date] = { date, orders: 0, revenue: 0 };
          acc[date].orders += 1;
          acc[date].revenue += (order.totalAmount || 0) / 100; // Store revenue in dollars
          return acc;
        }, {});

        // Sort dates to ensure chronological order for charts
        const sortedDates = Object.values(grouped).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        // Fill in missing dates for continuous graph appearance
        const allDates = {};
        if (sortedDates.length > 0) {
          let currentDate = new Date(sortedDates[0].date);
          const endDate = new Date(sortedDates[sortedDates.length - 1].date);
          while (currentDate <= endDate) {
            const dateString = currentDate.toISOString().slice(0, 10);
            allDates[dateString] = grouped[dateString] || { date: dateString, orders: 0, revenue: 0 };
            currentDate.setDate(currentDate.getDate() + 1);
          }
        }
        const filledAndSortedDates = Object.values(allDates).sort(
          (a, b) => new Date(a.date) - new Date(b.date)
        );

        setOrdersOverTime(filledAndSortedDates);
        setRevenueOverTime(filledAndSortedDates);

      } catch (err) {
        console.error("Error fetching analytics data:", err);
        setError("Failed to load analytics data. Please check console for details.");
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
  }, []);

  if (loading) return <div className="text-center py-20 text-gray-300 text-xl">Loading Analytics...</div>;
  if (error) return <div className="text-center py-20 text-red-400 text-xl">{error}</div>;

  const SummaryCard = ({ title, value, icon: Icon }) => (
    <article className="summary-card"> {/* Custom class for specific styling */}
      <div className="summary-card-icon-wrapper">
        {Icon && <Icon size={28} className="text-white" />}
      </div>
      <h2 className="text-lg font-semibold text-gray-300 mb-2 uppercase tracking-wide">{title}</h2>
      <p className="text-4xl font-bold text-white summary-card-value">{value}</p> {/* Custom class for value gradient */}
    </article>
  );

  return (
    <div className="dashboard-container"> {/* Uses main layout styling */}
      <header className="dashboard-header">
        <h1 className="text-4xl font-extrabold text-white uppercase tracking-wider">Store Analytics</h1>
      </header>

      <section className="summary-cards">
        <SummaryCard title="Total Revenue" value={`$${stats.totalRevenue.toFixed(2)}`} icon={DollarSign} />
        <SummaryCard title="Orders Count" value={stats.totalOrders} icon={ShoppingBag} />
        <SummaryCard title="Average Order Value" value={`$${stats.avgOrder.toFixed(2)}`} icon={TrendingUp} />
      </section>

      <section className="charts-section">
        <div className="chart-card"> {/* Custom class for specific styling */}
          <h3 className="text-xl font-bold text-white mb-6">Revenue Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={revenueOverTime} margin={{ top: 15, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#3a4154" vertical={false} /> {/* Only horizontal grid lines, darker */}
              <XAxis dataKey="date" stroke="#cbd5e0" tickFormatter={(dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
              <YAxis stroke="#cbd5e0" tickFormatter={(value) => `$${value.toFixed(0)}`} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }} 
                labelStyle={{ color: '#ffffff', fontWeight: 'bold' }} 
                itemStyle={{ color: '#e2e8f0' }}
                formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke="#d6bcfa" // Lighter, more vibrant purple for line
                strokeWidth={3}
                dot={{ r: 4, fill: '#8b5cf6', stroke: '#d6bcfa', strokeWidth: 2 }}
                activeDot={{ r: 7, fill: '#8b5cf6', stroke: '#d6bcfa', strokeWidth: 3 }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="chart-card"> {/* Custom class for specific styling */}
          <h3 className="text-xl font-bold text-white mb-6">Orders Over Time</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={ordersOverTime} margin={{ top: 15, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#3a4154" vertical={false} /> {/* Only horizontal grid lines */}
              <XAxis dataKey="date" stroke="#cbd5e0" tickFormatter={(dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} />
              <YAxis stroke="#cbd5e0" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.4)' }} 
                labelStyle={{ color: '#ffffff', fontWeight: 'bold' }} 
                itemStyle={{ color: '#e2e8f0' }}
                formatter={(value) => [value, 'Orders']}
              />
              <Bar dataKey="orders" fill="#34d399" radius={[8, 8, 0, 0]} /> {/* Lighter, more vibrant green for bars */}
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>
      <section className="dashboard-quick-actions">
        <h2 className="section-title">Quick Actions</h2>
        <div className="actions-grid">
          <Link 
            to="/admin/products/add" 
            className="action-button primary"
          >
            <PlusCircle className="mr-2" size={24} /> Add New Product
          </Link>
          <Link 
            to="/admin/settings"
            className="action-button secondary"
          >
            <Settings className="mr-2" size={24} /> Manage Settings
          </Link>
          <button 
            className="action-button secondary"
            onClick={() => alert('Manage Discount Codes functionality will be built later!')}
          >
            <Gift className="mr-2" size={24} /> Manage Discount Codes
          </button>
          <Link 
            to="/admin/analytics"
            className="action-button secondary"
          >
            <BarChart className="mr-2" size={24} /> View Analytics
          </Link>
          <Link 
            to="/admin/wallet"
            className="action-button secondary"
          >
            <Wallet2 className="mr-2" size={24} /> My Wallet
          </Link>
          <Link 
            to="/admin/shipment-center"
            className="action-button secondary"
          >
            <PackageCheck className="mr-2" size={24} /> Shipment Center
          </Link>
        </div>
      </section>
    </div>
  );
};

export default AnalyticsDashboard;
